import Step from "../../components/step/Step";
import { Form, Field, Formik } from "formik";
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  InputGroup,
  InputRightAddon,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Select,
} from "@chakra-ui/react";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import useStore from "../../storage/DataStore";
import { useIntl } from "react-intl";
import { translation } from "../../utils/translation";

function createAgeRange(start, end) {
  return Array(end - start + 1)
    .fill()
    .map((_, idx) => start + idx);
}

function Step12() {
  const intl = useIntl();
  let country = intl.locale;

  let system = "metric";

  if (country === "en" || country === "en-ie") {
    system = "metric";
  }

  const UserDataSchema = Yup.object().shape({
    weight: Yup.number()
      .max("300", translation("general.form.weight.required"))
      .required(translation("general.form.weight.required"))
      .typeError(translation("general.form.message.isNumber")),
    targetWeight: Yup.number()
      .max("300", translation("general.form.targetWeight.required"))
      .required(translation("general.form.targetWeight.required"))
      .typeError(translation("general.form.message.isNumber")),
    bodyHeight: Yup.number()
      .required(translation("general.form.bodySize.required"))
      .typeError(translation("general.form.message.isNumber")),
    age: Yup.number().required(translation("general.form.age.chooseAge")),
  });

  const ageRange = createAgeRange(18, 99);

  let navigate = useNavigate();

  return (
    <Step
      heading={translation("general.step12.headline")}
      description={translation("general.step12.text")}
      currentStep={9}
      backLink={"/step11"}
    >
      <Formik
        initialValues={{
          weight: "",
          targetWeight: "",
          bodyHeight: "",
          age: "",
        }}
        validationSchema={UserDataSchema}
        onSubmit={(values, actions) => {
          useStore.setState({
            weight: values.weight,
            targetWeight: values.targetWeight,
            bodyHeight: values.bodyHeight,
            age: values.age,
          });
          navigate("../loader", { replace: true });
        }}
      >
        {(props) => (
          <Form>
            {system === "metric" && (
              <>
                <Grid mt={5} templateColumns="repeat(2, 1fr)" gap={10} mb={5}>
                  <Box>
                    <Field name={"weight"}>
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={form.errors.weight && form.touched.weight}
                        >
                          <FormLabel fontWeight={"bold"} htmlFor="weight">
                            {translation("general.form.weight.label")}
                          </FormLabel>

                          <InputGroup>
                            <Input
                              pattern={"[0-9]*"}
                              inputMode={"number"}
                              {...field}
                              id="weight"
                            />

                            <InputRightAddon pointerEvents="none">
                              kg
                            </InputRightAddon>
                          </InputGroup>

                          <FormErrorMessage>
                            {form.errors.weight}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Box>

                  <Box>
                    <Field name={"targetWeight"}>
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={
                            form.errors.targetWeight &&
                            form.touched.targetWeight
                          }
                        >
                          <FormLabel fontWeight={"bold"} htmlFor="targetWeight">
                            {translation("general.form.targetWeight.label")}
                          </FormLabel>

                          <InputGroup>
                            <Input
                              pattern={"[0-9]*"}
                              inputMode={"number"}
                              {...field}
                              id="targetWeight"
                            />

                            <InputRightAddon pointerEvents="none">
                              kg
                            </InputRightAddon>
                          </InputGroup>

                          <FormErrorMessage>
                            {form.errors.targetWeight}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Box>
                </Grid>

                <Box mb={5}>
                  <Field name={"bodyHeight"}>
                    {({ field, form }) => (
                      <FormControl
                        isInvalid={
                          form.errors.bodyHeight && form.touched.bodyHeight
                        }
                      >
                        <FormLabel fontWeight={"bold"} htmlFor="bodyHeight">
                          {translation("general.form.bodySize.label")}
                        </FormLabel>

                        <InputGroup>
                          <Input
                            pattern={"[0-9]*"}
                            inputMode={"number"}
                            {...field}
                            id="bodyHeight"
                          />

                          <InputRightAddon pointerEvents="none">
                            cm
                          </InputRightAddon>
                        </InputGroup>

                        <FormErrorMessage>
                          {form.errors.bodyHeight}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Box>
              </>
            )}

            {system === "imperial" && (
              <>
                <Grid mt={5} templateColumns="repeat(2, 1fr)" gap={10} mb={5}>
                  <Box>
                    <Field name={"weight"}>
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={form.errors.weight && form.touched.weight}
                        >
                          <FormLabel fontWeight={"bold"} htmlFor="weight">
                            {translation("general.form.weight.label")} (st)
                          </FormLabel>

                          <InputGroup>
                            <NumberInput
                              {...field}
                              onChange={(val) =>
                                form.setFieldValue(field.name, val)
                              }
                              id="weight"
                              min={6}
                              max={25}
                              precision={1}
                              step={0.1}
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </InputGroup>

                          <FormErrorMessage>
                            {form.errors.weight}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Box>

                  <Box>
                    <Field name={"targetWeight"}>
                      {({ field, form }) => (
                        <FormControl
                          isInvalid={
                            form.errors.targetWeight &&
                            form.touched.targetWeight
                          }
                        >
                          <FormLabel fontWeight={"bold"} htmlFor="targetWeight">
                            {translation("general.form.targetWeight.label")}{" "}
                            (st)
                          </FormLabel>

                          <InputGroup>
                            <NumberInput
                              {...field}
                              onChange={(val) =>
                                form.setFieldValue(field.name, val)
                              }
                              id="targetWeight"
                              min={6}
                              max={25}
                              precision={1}
                              step={0.1}
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </InputGroup>

                          <FormErrorMessage>
                            {form.errors.targetWeight}
                          </FormErrorMessage>
                        </FormControl>
                      )}
                    </Field>
                  </Box>
                </Grid>

                <Box mb={5}>
                  <Field name={"bodyHeight"}>
                    {({ field, form }) => (
                      <FormControl
                        isInvalid={
                          form.errors.bodyHeight && form.touched.bodyHeight
                        }
                      >
                        <FormLabel fontWeight={"bold"} htmlFor="bodyHeight">
                          {translation("general.form.bodySize.label")} (ft)
                        </FormLabel>

                        <InputGroup>
                          <NumberInput
                            {...field}
                            onChange={(val) =>
                              form.setFieldValue(field.name, val)
                            }
                            id="bodyHeight"
                            min={4}
                            max={7}
                            precision={1}
                            step={0.1}
                            width={"100%"}
                          >
                            <NumberInputField />
                            <NumberInputStepper>
                              <NumberIncrementStepper />
                              <NumberDecrementStepper />
                            </NumberInputStepper>
                          </NumberInput>
                        </InputGroup>

                        <FormErrorMessage>
                          {form.errors.bodyHeight}
                        </FormErrorMessage>
                      </FormControl>
                    )}
                  </Field>
                </Box>
              </>
            )}

            <Box>
              <Field name={"age"}>
                {({ field, form }) => (
                  <FormControl isInvalid={form.errors.age && form.touched.age}>
                    <FormLabel fontWeight={"bold"} htmlFor="age">
                      {translation("general.form.age.label")}
                    </FormLabel>

                    <InputGroup>
                      <Select
                        fontSize={"sm"}
                        {...field}
                        id="age"
                      >
                        <option value="">
                          {translation("general.form.age.chooseAge")}
                        </option>
                        <option value="17">
                          {translation("general.form.age.under18")}
                        </option>
                        {ageRange.map((index) => {
                          return (
                            <option key={index} value={index}>
                              {index} {translation("general.form.age.option")}
                            </option>
                          );
                        })}
                      </Select>
                    </InputGroup>

                    <FormErrorMessage>{form.errors.age}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
            </Box>

            <Button
              bg="brand"
              color={"white"}
              size="md"
              mt={5}
              w={"100%"}
              type="submit"
            >
              {translation("general.form.button.next")}
            </Button>
          </Form>
        )}
      </Formik>
    </Step>
  );
}

export default Step12;
